import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Search, Filter, User } from "lucide-react";

const POSITIONS = ["QB","RB","WR","TE","OL","DL","LB","DB","K","P","ATH"];
const CLASS_YEARS = ["2026","2027","2028","2029","2030","2031"];
const STATUSES = ["uncommitted","interested","committed","signed"];

export default function PlayersDirectory() {
  const [players, setPlayers] = useState([]);
  const [filters, setFilters] = useState({});

  const load = async (f = filters) => {
    const params = new URLSearchParams();
    Object.entries(f).forEach(([k,v]) => { if (v) params.set(k, v); });
    const { data } = await api.get(`/players?${params}`);
    setPlayers(data);
  };
  useEffect(() => { load({}); }, []);

  const applyFilters = (e) => { e.preventDefault(); load(filters); };
  const set = (k,v) => setFilters({ ...filters, [k]: v });

  return (
    <div>
      <section className="section-dark py-20">
        <div className="container-app">
          <div className="label-tag mb-4">Coaches Directory</div>
          <h1 className="font-heading font-black uppercase text-5xl md:text-7xl leading-none">Scout the <span className="metallic-gold">Top 20%.</span></h1>
          <p className="mt-6 text-white/70 max-w-2xl text-lg">College coaches: filter, evaluate, and connect with recruits ready for the next level.</p>
        </div>
      </section>

      <section className="section-light py-14">
        <div className="container-app grid lg:grid-cols-4 gap-6">
          <form onSubmit={applyFilters} className="card-light p-6 lg:col-span-1 h-fit space-y-5 sticky top-24">
            <div className="font-heading uppercase font-bold text-lg flex items-center gap-2"><Filter size={16}/> Filters</div>
            <SelectF label="Position" v={filters.position} onC={(v)=>set("position",v)} opts={POSITIONS} testid="filter-position" />
            <SelectF label="Class Year" v={filters.class_year} onC={(v)=>set("class_year",v)} opts={CLASS_YEARS} testid="filter-class" />
            <TF label="School" v={filters.school} onC={(v)=>set("school",v)} testid="filter-school" />
            <TF label="Location" v={filters.location} onC={(v)=>set("location",v)} testid="filter-location" />
            <TF label="Min GPA" v={filters.min_gpa} onC={(v)=>set("min_gpa",v)} testid="filter-gpa" />
            <SelectF label="Recruiting Status" v={filters.recruiting_status} onC={(v)=>set("recruiting_status",v)} opts={STATUSES} testid="filter-status" />
            <TF label="Search Name" v={filters.q} onC={(v)=>set("q",v)} testid="filter-name" />
            <button className="btn-gold w-full justify-center" data-testid="apply-filters"><Search size={16}/> Apply</button>
            <button type="button" className="btn-ghost-dark w-full justify-center" onClick={()=>{ setFilters({}); load({}); }} data-testid="clear-filters">Clear</button>
          </form>

          <div className="lg:col-span-3">
            <div className="mb-6 text-neutral-600 text-sm font-mono">{players.length} athletes</div>
            <div className="grid sm:grid-cols-2 gap-4" data-testid="players-grid">
              {players.map(p => (
                <Link key={p.id} to={`/players/${p.id}`} className="card-light hover-lift p-6 flex gap-4" data-testid={`player-card-${p.id}`}>
                  <div className="w-20 h-20 bg-neutral-100 border border-black/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {p.photo_url ? <img src={p.photo_url} className="w-full h-full object-cover" alt={p.name}/> : <User className="text-neutral-400"/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading uppercase font-bold text-lg truncate">{p.name}</div>
                    <div className="text-xs text-neutral-500 mt-1">{p.position || "—"} · Class of {p.class_year || "—"}</div>
                    <div className="text-xs text-neutral-500">{p.school || "—"}</div>
                    <div className="text-xs mt-2 flex gap-2 flex-wrap">
                      {p.height && <span className="px-2 py-0.5 border border-black/10">{p.height}</span>}
                      {p.weight && <span className="px-2 py-0.5 border border-black/10">{p.weight}lb</span>}
                      {p.gpa && <span className="px-2 py-0.5 border border-black/10">GPA {p.gpa}</span>}
                    </div>
                  </div>
                </Link>
              ))}
              {players.length === 0 && <div className="text-neutral-400 col-span-2 py-16 text-center">No athletes match filters.</div>}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function TF({ label, v, onC, testid }) {
  return (
    <div>
      <label className="label-tag" style={{ color: "#000" }}>{label}</label>
      <input value={v||""} onChange={(e)=>onC(e.target.value)} className="input-sharp mt-1" data-testid={testid} />
    </div>
  );
}
function SelectF({ label, v, onC, opts, testid }) {
  return (
    <div>
      <label className="label-tag" style={{ color: "#000" }}>{label}</label>
      <select value={v||""} onChange={(e)=>onC(e.target.value)} className="input-sharp mt-1" data-testid={testid}>
        <option value="">Any</option>
        {opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
